import { joinClassNames, truncateAddress } from '@vertex-protocol/web-common';
import { Icons, PrimaryButton } from '@vertex-protocol/web-ui';
import { ActionToast } from 'client/components/Toast/ActionToast/ActionToast';
import { DEFAULT_TOAST_TTL } from 'client/components/Toast/consts';
import { QRCodeSVG } from 'qrcode.react';
import { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { TokenIcon } from 'client/modules/collateral/nadoDeposit/components/NadoTokenSelect';
import { FundingTokenSymbol } from 'client/modules/collateral/nadoDeposit/config/fundingRoutes';
import { isReceiveWalletMismatch } from 'client/modules/collateral/nadoDeposit/utils/receiveSession';

interface Props {
  selectedTokenSymbol: FundingTokenSymbol;
  receiveAddress: `0x${string}` | undefined;
  connectedAddress: `0x${string}` | undefined;
  availableBalance: string;
  amount: string;
  isContinueDisabled: boolean;
  inputError?: string;
  onTokenSelected: (tokenSymbol: FundingTokenSymbol) => void;
  onAmountChange: (amount: string) => void;
  onUseAvailableBalance: () => void;
  onContinue: () => void;
  onRefreshBalance: () => void;
  onRestartReceiveSession: () => void;
}

const FUNDING_TOKENS: FundingTokenSymbol[] = ['USDT', 'USDC'];

export function NadoStablecoinReceivePanel({
  selectedTokenSymbol,
  receiveAddress,
  connectedAddress,
  availableBalance,
  amount,
  isContinueDisabled,
  inputError,
  onTokenSelected,
  onAmountChange,
  onUseAvailableBalance,
  onContinue,
  onRefreshBalance,
  onRestartReceiveSession,
}: Props) {
  const [copyStatus, setCopyStatus] = useState<string | undefined>();
  const addressInputRef = useRef<HTMLInputElement>(null);
  const walletMismatch = isReceiveWalletMismatch(
    receiveAddress
      ? {
          receiveAddress,
          sessionId: receiveAddress,
          startedAtMs: 0,
        }
      : undefined,
    connectedAddress,
  );

  async function copyAddress() {
    if (!receiveAddress) {
      return;
    }
    const didCopy = await copyTextToClipboard(
      receiveAddress,
      addressInputRef.current,
    );
    setCopyStatus(
      didCopy ? 'Address copied' : 'Address selected - press Cmd+C',
    );
    if (didCopy) {
      showAddressCopiedToast();
    }
    window.setTimeout(() => setCopyStatus(undefined), 2500);
  }

  return (
    <section className="flex flex-col gap-y-4" aria-label="Stablecoin deposit">
      <div className="flex gap-2">
        {FUNDING_TOKENS.map((tokenSymbol) => {
          const isActive = tokenSymbol === selectedTokenSymbol;
          return (
            <button
              key={tokenSymbol}
              type="button"
              onClick={() => onTokenSelected(tokenSymbol)}
              className={joinClassNames(
                'border-stroke flex flex-1 items-center justify-center gap-x-2 rounded-sm border px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-surface-card text-text-primary'
                  : 'bg-surface-1 text-text-tertiary hover:text-text-secondary',
              )}
            >
              <TokenIcon symbol={tokenSymbol} />
              {tokenSymbol}
            </button>
          );
        })}
      </div>

      <div className="bg-surface-2 flex flex-col items-center gap-y-3 rounded-sm p-4">
        <div className="bg-surface-card rounded-sm p-3">
          {receiveAddress ? (
            <QRCodeSVG value={receiveAddress} size={196} marginSize={2} />
          ) : (
            <div className="bg-surface-3 text-text-tertiary flex size-[196px] items-center justify-center rounded-sm text-xs">
              Connect Wallet
            </div>
          )}
        </div>

        <div className="border-stroke bg-surface-card flex w-full items-center gap-x-2 rounded-sm border px-3 py-2">
          {receiveAddress ? (
            <input
              ref={addressInputRef}
              aria-label="Deposit address"
              readOnly
              value={receiveAddress}
              onFocus={(event) => event.currentTarget.select()}
              className="text-text-secondary min-w-0 flex-1 bg-transparent text-xs outline-none"
            />
          ) : (
            <span className="text-text-secondary min-w-0 flex-1 break-all text-xs">
              No wallet connected
            </span>
          )}
          <button
            type="button"
            aria-label="Copy address"
            disabled={!receiveAddress}
            onClick={copyAddress}
            className="text-text-tertiary hover:text-text-secondary disabled:opacity-40"
          >
            <Icons.Copy className="size-4" />
          </button>
        </div>

        {copyStatus && (
          <p className="text-positive text-xs" role="status">
            {copyStatus}
          </p>
        )}

        <p className="text-warning text-xs">
          Deposit only {selectedTokenSymbol} on Ethereum to this address.
        </p>
      </div>

      {walletMismatch ? (
        <div className="border-warning bg-warning/10 flex flex-col gap-y-2 rounded-sm border p-3">
          <p className="text-warning text-xs">
            Reconnect the wallet that owns this deposit address (
            {receiveAddress ? truncateAddress(receiveAddress) : 'unknown'}) or restart with the current wallet.
          </p>
          <button
            type="button"
            onClick={onRestartReceiveSession}
            className="text-text-primary border-stroke hover:bg-surface-2 rounded-sm border px-3 py-2 text-sm"
          >
            Restart With Current Wallet
          </button>
        </div>
      ) : null}

      <div className="bg-surface-2 flex flex-col gap-y-3 rounded-sm p-3">
        <div className="flex items-center justify-between">
          <span className="text-text-tertiary text-xs">Detected on Ethereum</span>
          <button
            type="button"
            onClick={onRefreshBalance}
            className="text-text-secondary hover:text-text-primary text-xs"
          >
            Refresh
          </button>
        </div>
        <div className="flex items-center justify-between gap-x-3">
          <span className="text-text-primary text-sm font-medium">
            {availableBalance} {selectedTokenSymbol}
          </span>
          <button
            type="button"
            onClick={onUseAvailableBalance}
            className="text-primary text-xs font-medium"
          >
            Max
          </button>
        </div>
        <label className="flex flex-col gap-y-1">
          <span className="text-text-tertiary text-xs">Amount to fund Nado</span>
          <input
            value={amount}
            onChange={(event) => onAmountChange(event.target.value)}
            placeholder="0.00"
            inputMode="decimal"
            className="bg-surface-card text-text-primary border-stroke rounded-sm border px-3 py-2 text-right text-base outline-none"
          />
        </label>
        {inputError && <p className="text-negative text-xs">{inputError}</p>}
      </div>

      <PrimaryButton
        disabled={!receiveAddress || walletMismatch || isContinueDisabled}
        onClick={onContinue}
      >
        Continue to Bridge
      </PrimaryButton>
    </section>
  );
}

function showAddressCopiedToast() {
  toast.custom(
    (t) => (
      <ActionToast.Container visible={t.visible}>
        <ActionToast.TextHeader
          variant="success"
          onDismiss={() => toast.dismiss(t.id)}
        >
          Address Copied
        </ActionToast.TextHeader>
        <ActionToast.Separator variant="success" ttl={DEFAULT_TOAST_TTL} />
        <ActionToast.Body variant="success">
          Stablecoin deposit address copied to clipboard.
        </ActionToast.Body>
      </ActionToast.Container>
    ),
    {
      duration: DEFAULT_TOAST_TTL,
      id: 'nado_stablecoin_address_copied',
    },
  );
}

async function copyTextToClipboard(
  value: string,
  visibleInput?: HTMLInputElement | null,
): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      // Embedded browser panes can expose navigator.clipboard but deny writeText.
      // Fall through to the user-gesture textarea copy path.
    }
  }

  if (visibleInput && copySelectedInputText(visibleInput)) {
    return true;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    textarea.setSelectionRange(0, textarea.value.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

function copySelectedInputText(input: HTMLInputElement) {
  input.focus();
  input.select();
  input.setSelectionRange(0, input.value.length);
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  }
}
